import { useState, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'
import { DATE_FORMAT } from '../config/constants'
import { STATUS } from '../config/taskStatus'
import { createTask, readTasks, updateTask } from '../utils/taskUtils'
import { useSelector } from 'react-redux'
import { readSystemUsersAggregate } from '../../../../../../Admin/system-users/utils/utils'
import { useNotifications } from '../../../context/NotificationsContext'
import { useWebSocket } from '../../../context/WebSocketContext'

const useTasks = () => {
  const coreState = useSelector((state) => state.core)
  const authState = useSelector((state) => state.auth)
  const [tasks, setTasks] = useState([])
  const [medicalProfs, setMedicalProfs] = useState([])
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const { addNotification } = useNotifications()
  const { sendMessage } = useWebSocket()

  // Initialize tasks from database
  useEffect(() => {
    handleFetchTasks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for WebSocket messages related to tasks
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      const { type, action, data } = event.detail

      if (type === 'TASK') {
        switch (action) {
          case 'CREATE':
            // Add the new task to the list
            setTasks((prev) => {
              // Check if task already exists to avoid duplicates
              if (prev.some((task) => task._id === data.task._id)) {
                return prev
              }

              return [
                ...prev,
                {
                  ...data.task,
                  assignedBy: data.task.assignedBy || authState.agiliteUser,
                  assignedTo: medicalProfs.find((prof) => prof._id === data.task.assignee)
                }
              ]
            })
            break
          case 'UPDATE':
            // Update the task in the list
            setTasks((prev) =>
              prev.map((task) =>
                task._id === data.task._id
                  ? {
                      ...data.task,
                      assignedBy: data.task.assignedBy || authState.agiliteUser,
                      assignedTo: medicalProfs.find((prof) => prof._id === data.task.assignee)
                    }
                  : task
              )
            )
            break
          case 'DELETE':
            // Remove the task from the list
            setTasks((prev) => prev.filter((task) => task._id !== data.taskId))
            break
          case 'COMPLETE':
            // Mark the task as completed
            setTasks((prev) =>
              prev.map((task) =>
                task._id === data.taskId
                  ? {
                      ...task,
                      status: STATUS.COMPLETED
                    }
                  : task
              )
            )
            break
          default:
            break
        }
      }
    }

    window.addEventListener('websocket-message', handleWebSocketMessage)

    return () => {
      window.removeEventListener('websocket-message', handleWebSocketMessage)
    }
  }, [authState.agiliteUser, medicalProfs])

  const handleFetchTasks = async () => {
    setLoadingTasks(true)
    try {
      const tasks = await readTasks({
        $or: [{ 'audit.createdBy': authState.agiliteUser?._id }, { assignee: authState.agiliteUser?._id }]
      })
      const tmpMedicalProfs = await readSystemUsersAggregate({
        $and: [
          { 'extraData.role.type': { $ne: 'patient' } },
          { 'extraData.role.type': { $ne: null } },
          { 'extraData.entities': coreState.entity._id }
        ]
      })

      setMedicalProfs(tmpMedicalProfs)

      // Update tasks with assignedBy and assignedTo information
      const updatedTasks = tasks.map((task) => ({
        ...task,
        assignedBy: authState.agiliteUser,
        assignedTo: tmpMedicalProfs.find((prof) => prof._id === task.assignee)
      }))

      setTasks(updatedTasks)
    } catch (error) {
      console.error('Error fetching tasks:', error)
    } finally {
      setLoadingTasks(false)
    }
  }

  // Check for overdue tasks every minute
  useEffect(() => {
    const checkOverdueTasks = () => {
      const now = dayjs()
      setTasks((prevTasks) => {
        const hasChanges = prevTasks.some(
          (task) => task.status === STATUS.PENDING && dayjs(task.dueDate, DATE_FORMAT).isBefore(now, 'day')
        )

        if (!hasChanges) return prevTasks // Skip update if no changes needed

        return prevTasks.map((task) => {
          if (task.status === STATUS.PENDING && dayjs(task.dueDate, DATE_FORMAT).isBefore(now, 'day')) {
            return { ...task, status: STATUS.OVERDUE }
          }
          return task
        })
      })
    }

    // Initial check
    checkOverdueTasks()

    // Set up interval for checking
    const interval = setInterval(checkOverdueTasks, 60000)
    return () => clearInterval(interval)
  }, [])

  const handleCreateTask = async (values) => {
    try {
      const newTask = {
        ...values,
        status: 'active'
      }
      const createdTask = await createTask(newTask)
      setIsCreateModalVisible(false)

      // Add the new task to the local state
      const updatedTask = {
        ...createdTask,
        assignedBy: authState.agiliteUser,
        assignedTo: medicalProfs.find((prof) => prof._id === createdTask.assignee)
      }

      setTasks((prev) => [...prev, updatedTask])

      // Notify other users through WebSocket
      sendMessage({
        type: 'TASK',
        action: 'CREATE',
        data: {
          task: updatedTask
        }
      })

      addNotification({
        title: 'Task Created',
        description: 'Your task has been created successfully',
        type: 'success'
      })
    } catch (error) {
      console.error('Error creating task:', error)
      addNotification({
        title: 'Error',
        description: 'Failed to create task',
        type: 'error'
      })
    }
  }

  const editTask = useCallback(
    (taskId, values) => {
      // Make sure dueDate is properly formatted regardless of whether it's a dayjs object or string
      const formattedDueDate = values.dueDate
        ? typeof values.dueDate === 'object' && typeof values.dueDate.format === 'function'
          ? values.dueDate.format(DATE_FORMAT)
          : values.dueDate
        : null

      // Clone the tasks array to avoid reference issues
      const updatedTasks = [...tasks]

      // Find the task by _id or id (depending on what's available)
      const taskIndex = updatedTasks.findIndex((task) => task._id === taskId || task.id === taskId)

      if (taskIndex !== -1) {
        // Update the existing task instead of creating a new one
        const updatedTask = {
          ...updatedTasks[taskIndex],
          ...values,
          modifiedAt: new Date().toISOString(),
          modifiedBy: authState.agiliteUser?._id,
          dueDate: formattedDueDate
        }

        updatedTasks[taskIndex] = updatedTask

        // Update the state with the modified array
        setTasks(updatedTasks)

        // Notify other users through WebSocket
        sendMessage({
          type: 'TASK',
          action: 'UPDATE',
          data: {
            task: {
              ...updatedTask,
              assignedBy: authState.agiliteUser,
              assignedTo: medicalProfs.find((prof) => prof._id === updatedTask.assignee)
            }
          }
        })
      }
    },
    // eslint-disable-next-line
    [authState.agiliteUser?._id, tasks, medicalProfs, sendMessage]
  )

  const deleteTask = useCallback(
    (taskId) => {
      setTasks((prev) => prev.filter((task) => task._id !== taskId && task.id !== taskId))

      // Notify other users through WebSocket
      sendMessage({
        type: 'TASK',
        action: 'DELETE',
        data: {
          taskId
        }
      })
    },
    [sendMessage]
  )

  const handleCompleteTask = async (taskId) => {
    const tmpTasks = JSON.parse(JSON.stringify(tasks))
    const taskIndex = tmpTasks.findIndex((task) => task._id === taskId)

    try {
      const newRecord = await updateTask(taskId, { status: STATUS.COMPLETED })

      addNotification({
        title: 'Task Completed',
        description: `Task "${newRecord.description}" has been marked as complete.`
      })
      tmpTasks[taskIndex].status = STATUS.COMPLETED
      setTasks(tmpTasks)

      // Notify other users through WebSocket
      sendMessage({
        type: 'TASK',
        action: 'COMPLETE',
        data: {
          taskId: taskId
        }
      })
    } catch (e) {
      console.error('Error completing task:', e)
    }
  }

  const handleUpdateTask = async (taskId, values) => {
    try {
      const updatedTask = await updateTask(taskId, values)

      // Update the task in the local state
      setTasks((prev) =>
        prev.map((task) =>
          task._id === taskId
            ? {
                ...updatedTask,
                assignedBy: authState.agiliteUser,
                assignedTo: medicalProfs.find((prof) => prof._id === updatedTask.assignee)
              }
            : task
        )
      )

      // Notify other users through WebSocket
      sendMessage({
        type: 'TASK',
        action: 'UPDATE',
        data: {
          task: {
            ...updatedTask,
            assignedBy: authState.agiliteUser,
            assignedTo: medicalProfs.find((prof) => prof._id === updatedTask.assignee)
          }
        }
      })

      addNotification({
        title: 'Task Updated',
        description: 'Your task has been updated successfully',
        type: 'success'
      })
    } catch (error) {
      console.error('Error updating task:', error)
      addNotification({
        title: 'Error',
        description: 'Failed to update task',
        type: 'error'
      })
    }
  }

  const handleDeleteTask = async (taskId) => {
    try {
      // Delete the task from the database
      await updateTask(taskId, { status: 'deleted' })

      // Remove the task from the local state
      setTasks((prev) => prev.filter((task) => task._id !== taskId))

      // Notify other users through WebSocket
      sendMessage({
        type: 'TASK',
        action: 'DELETE',
        data: {
          taskId
        }
      })

      addNotification({
        title: 'Task Deleted',
        description: 'Your task has been deleted successfully',
        type: 'success'
      })
    } catch (error) {
      console.error('Error deleting task:', error)
      addNotification({
        title: 'Error',
        description: 'Failed to delete task',
        type: 'error'
      })
    }
  }

  return {
    handleFetchTasks,
    tasks,
    handleCreateTask,
    editTask,
    deleteTask,
    loadingTasks,
    medicalProfs,
    isCreateModalVisible,
    setIsCreateModalVisible,
    handleCompleteTask,
    handleUpdateTask,
    handleDeleteTask
  }
}

export default useTasks
